import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, debounceTime, forkJoin, groupBy, mergeMap, of } from 'rxjs';
import { WorkflowGraphStore } from './workflow-graph.store';
import { WorkflowApiService } from '../../services/workflow-api.service';
import { WorkflowBuilderApiService } from '../../services/workflow-builder-api.service';
import { WorkflowTestingApiService } from '../../services/workflow-testing-api.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { WorkflowCrmEventOptionResponse, WorkflowNodeCatalogItemResponse, WorkflowNodeCatalogResponse } from '../../models/workflow-builder.model';
import { WorkflowActivationChecklistResponse } from '../../models/workflow-testing.model';
import { WorkflowResponse } from '../../models/workflow.model';
import { buildWorkflowVariableCatalog } from '../models/workflow-variable-catalog';

const AUTOSAVE_DEBOUNCE_MS = 2000;
const POSITION_DEBOUNCE_MS = 400;

interface PositionPatch {
  nodeKey: string;
  positionX: number;
  positionY: number;
}

/** Extrae el mensaje de error legible de una respuesta del backend. */
function extractApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { error?: { message?: string; details?: Array<{ message?: string }> } };
  const details = e?.error?.details;
  if (Array.isArray(details) && details.length && details[0]?.message) {
    return details[0].message;
  }
  return e?.error?.message || fallback;
}

/**
 * Estado y persistencia del editor de workflows, compartido por el shell (barra superior)
 * y las vistas del builder. Lo provee el shell junto con el `WorkflowGraphStore`.
 *
 * Se calca de `ChatflowEditorService` y se adapta al VERSIONADO de workflow (gotcha §6):
 * el editor opera SIEMPRE sobre la versión editable que devuelve `GET /builder/graph`
 * (`resolveEditableContext` en el backend) y guarda contra ese `workflowVersionId`, sin
 * pisar la versión publicada (`publishedVersionId`).
 */
@Injectable()
export class WorkflowEditorService {
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly builderApi = inject(WorkflowBuilderApiService);
  private readonly testingApi = inject(WorkflowTestingApiService);
  private readonly store = inject(WorkflowGraphStore);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _workflowId = signal<number | null>(null);
  private readonly _workflow = signal<WorkflowResponse | null>(null);
  private readonly _catalog = signal<WorkflowNodeCatalogItemResponse[]>([]);
  private readonly _crmEvents = signal<WorkflowCrmEventOptionResponse[]>([]);
  private readonly _checklist = signal<WorkflowActivationChecklistResponse | null>(null);
  private readonly _loading = signal(true);
  private readonly _fatalError = signal('');
  private readonly _saving = signal(false);
  private readonly _validating = signal(false);

  readonly workflowId = this._workflowId.asReadonly();
  readonly workflow = this._workflow.asReadonly();
  readonly catalog = this._catalog.asReadonly();
  /**
   * Catálogo de eventos del CRM que devuelve `GET /builder/catalog` (etiqueta, descripción, grupo
   * y aplicabilidad al `objectType` del workflow). Es la FUENTE ÚNICA de los disparadores de
   * evento: el frontend ya no mantiene una lista de claves hardcodeada.
   *
   * Llega vacío si el backend es anterior al campo `crmEvents` o si el catálogo falló: en ese caso
   * el inspector lo dice en vez de inventarse opciones.
   */
  readonly crmEvents = this._crmEvents.asReadonly();
  /** Checklist de activación (por qué no se puede activar). Lo consume la validation-bar. */
  readonly checklist = this._checklist.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly fatalError = this._fatalError.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly validating = this._validating.asReadonly();

  /** El `workflowVersionId` editable, expuesto desde el store (fuente única). */
  readonly versionId = this.store.versionId;
  /** Nodos del grafo abierto: los necesita el nodo de notificación para saber de qué pasos informa. */
  readonly nodes = this.store.nodes;

  /**
   * Tokens `{{...}}` disponibles para el variable-picker, derivados del `objectType` del
   * workflow (no hay endpoint que los liste — ver GAP DE API). Es el equivalente workflow del
   * `variables()` de chatflow.
   */
  readonly variables = computed(() => buildWorkflowVariableCatalog(this._workflow()?.objectType ?? null));

  /**
   * No se edita si el workflow está ACTIVE/ARCHIVED (hay que pausarlo primero), si el
   * editor está cargando, o si hubo un error fatal de carga. Igual criterio que el builder
   * viejo (`isReadOnly`), pero derivado por signals.
   */
  readonly readOnly = computed(() => {
    if (this._fatalError() || this._loading()) {
      return true;
    }
    const status = this._workflow()?.status;
    return status === 'ACTIVE' || status === 'ARCHIVED';
  });

  readonly readOnlyMessage = computed(() => {
    const status = this._workflow()?.status;
    if (status === 'ACTIVE') {
      return 'No se puede editar un workflow activo. Pausa el workflow primero.';
    }
    if (status === 'ARCHIVED') {
      return 'No se puede editar un workflow archivado.';
    }
    return '';
  });

  private readonly positionPatch$ = new Subject<PositionPatch>();

  /**
   * Temporizador del autosave. Es un `setTimeout` y no un `debounceTime`: el debounce de RxJS
   * no se puede VACIAR (solo cancelar), y al destruirse el builder `takeUntilDestroyed` se
   * llevaba por delante el guardado pendiente — lo último escrito no llegaba nunca al backend.
   */
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Hay un PUT en vuelo y llegaron cambios nuevos: hay que volver a guardar al terminar. */
  private saveAgain = false;

  constructor() {
    // Cambiar de nodo seleccionado (o cerrar el inspector) vacía lo pendiente: el usuario ya
    // no ve el campo que acaba de editar y espera que se haya guardado.
    let lastSelectionKey = '';
    effect(() => {
      const selection = this.store.selection();
      const key = selection ? `${selection.kind}:${selection.kind === 'node' ? selection.nodeKey : selection.edgeId}` : '';
      if (key !== lastSelectionKey) {
        lastSelectionKey = key;
        // `untracked`: `save()` lee `dirty()`/`saving()`; sin esto el effect se re-dispararía en
        // cada tecla y el autosave dejaría de estar debounceado.
        untracked(() => this.flushAutosave());
      }
    });

    // Debounce POR NODO: arrastrar tres nodos distintos no debe hacer que el último
    // cancele los PATCH de los anteriores.
    this.positionPatch$
      .pipe(
        groupBy((patch) => patch.nodeKey),
        mergeMap((group) => group.pipe(debounceTime(POSITION_DEBOUNCE_MS))),
        takeUntilDestroyed()
      )
      .subscribe((patch) => this.persistPosition(patch));

    // Salir del builder (navegación SPA) destruye este servicio con el shell: se vacía antes de
    // irse. El PUT no está atado al inyector (`subscribe` suelto), así que sobrevive al destroy.
    this.destroyRef.onDestroy(() => this.flushAutosave());

    // Cerrar/recargar la pestaña: mejor esfuerzo, el navegador puede cortar la petición.
    const onBeforeUnload = () => this.flushAutosave();
    window.addEventListener('beforeunload', onBeforeUnload);
    this.destroyRef.onDestroy(() => window.removeEventListener('beforeunload', onBeforeUnload));
  }

  load(workflowId: number): void {
    this._workflowId.set(workflowId);
    this._loading.set(true);
    this._fatalError.set('');

    forkJoin({
      workflow: this.workflowApi.getById(workflowId),
      graph: this.builderApi.getGraph(workflowId),
      // El catálogo no debe tumbar el editor si falla: el resto sigue usable.
      catalog: this.builderApi
        .getCatalog(workflowId)
        .pipe(catchError(() => of({ items: [], crmEvents: [] } as WorkflowNodeCatalogResponse))),
      // El checklist es best-effort: alimenta la validation-bar pero no bloquea la carga.
      checklist: this.testingApi.getActivationChecklist(workflowId).pipe(catchError(() => of(null)))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this._workflow.set(result.workflow);
          this._catalog.set(result.catalog.items ?? []);
          this._crmEvents.set(result.catalog.crmEvents ?? []);
          this._checklist.set(result.checklist);
          this.store.loadFromGraph(result.graph);
          this._loading.set(false);
        },
        error: (err) => {
          this._fatalError.set(
            extractApiErrorMessage(err, 'No se pudo cargar el editor del workflow. Vuelve a intentarlo en unos minutos.')
          );
          this._loading.set(false);
        }
      });
  }

  setWorkflow(workflow: WorkflowResponse): void {
    this._workflow.set(workflow);
  }

  queuePositionPatch(patch: PositionPatch): void {
    this.positionPatch$.next(patch);
  }

  queueAutosave(): void {
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
    }
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      this.save(true);
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  /**
   * Vacía AHORA el autosave pendiente. Se llama en cada salida del editor —cambiar de nodo,
   * cerrar el inspector, salir del builder, cerrar la pestaña— para que nunca quede una edición
   * atrapada en la ventana de 2 s del debounce.
   *
   * Es seguro llamarlo de más: `save(true)` no hace nada si no hay cambios pendientes.
   */
  flushAutosave(): void {
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    this.save(true);
  }

  private persistPosition(patch: PositionPatch): void {
    const workflowId = this._workflowId();
    const versionId = this.store.versionId();
    const node = this.store.findNode(patch.nodeKey);
    if (!workflowId || versionId == null || !node?.id) {
      // Nodo sin id de BD todavía: su posición viaja en el próximo PUT del grafo.
      this.queueAutosave();
      return;
    }
    this.builderApi
      .updateNodePosition(workflowId, node.id, {
        workflowVersionId: versionId,
        positionX: patch.positionX,
        positionY: patch.positionY
      })
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        if (result) {
          this.store.notePositionPersisted();
          return;
        }
        // El PATCH es best-effort: si falla, se fuerza el PUT (el autosave normal ignora
        // los cambios no estructurales).
        this.save(false);
      });
  }

  /**
   * PUT del grafo completo: REPLACE y atómico. Reconcilia con la respuesta (los nodos
   * nuevos reciben su id de BD y los ids de arista rotan).
   */
  save(isAutosave: boolean): void {
    const workflowId = this._workflowId();
    if (!workflowId || this.readOnly() || this.store.versionId() == null) {
      return;
    }
    if (isAutosave && !this.store.dirty()) {
      return;
    }
    if (this._saving()) {
      // Ya hay un PUT en vuelo. Antes se descartaba el guardado en silencio y lo tecleado en ese
      // hueco no se persistía nunca; ahora se reencola para cuando el que viaja termine.
      this.saveAgain = true;
      return;
    }

    // Un guardado manual cancela el autosave pendiente: si no, saldría un segundo PUT idéntico.
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }

    this._saving.set(true);
    this.saveAgain = false;
    this.store.setSaveState('saving');
    // Revisión del grafo en el momento de enviar: si cambia mientras el PUT viaja, la respuesta
    // no puede reemplazar el estado local (borraría lo que el usuario escribió en ese hueco).
    const revisionAtSend = this.store.revision();

    this.builderApi.saveGraph(workflowId, this.store.toSaveRequest()).subscribe({
      next: (graph) => {
        const editedWhileSaving = this.store.revision() !== revisionAtSend;
        if (editedWhileSaving) {
          this.store.adoptServerIds(graph);
          this.saveAgain = true;
        } else {
          this.store.loadFromGraph(graph);
          this.store.markSaved();
        }
        this._saving.set(false);
        if (!isAutosave) {
          this.toast.updated('Workflow');
        }
        if (this.saveAgain) {
          this.saveAgain = false;
          this.save(true);
        }
      },
      error: (err) => {
        this._saving.set(false);
        this.saveAgain = false;
        this.store.setSaveState('error');
        this.toast.error({ title: 'Error al guardar', message: extractApiErrorMessage(err, 'No se pudo guardar el workflow.') });
      }
    });
  }

  /** Valida la versión editable contra el backend y refleja los avisos en el store. */
  validate(): void {
    const workflowId = this._workflowId();
    const versionId = this.store.versionId();
    if (!workflowId || versionId == null || this._validating()) {
      return;
    }
    this._validating.set(true);
    // Se combinan validación de nodos (para el store/canvas) y checklist de activación (para
    // la validation-bar). El checklist es best-effort: si falla, la validación de nodos sigue.
    forkJoin({
      validation: this.builderApi.validate(workflowId, versionId),
      checklist: this.testingApi.getActivationChecklist(workflowId).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ validation, checklist }) => {
        this.store.setValidation(validation);
        this._checklist.set(checklist);
        this._validating.set(false);
        if (validation.status === 'VALID') {
          this.toast.success({ title: 'Workflow válido', message: 'La estructura del flujo es correcta.' });
        } else {
          this.toast.warning({
            title: 'Validación completada',
            message: `El flujo tiene ${validation.errors.length} aviso(s) pendiente(s).`
          });
        }
      },
      error: (err) => {
        this._validating.set(false);
        this.toast.error({ title: 'Error', message: extractApiErrorMessage(err, 'No se pudo validar el workflow.') });
      }
    });
  }

  /** Ítem del catálogo por (tipo, subtipo), para hidratar un nodo recién añadido. */
  catalogItem(nodeType: string, nodeSubtype: string | null): WorkflowNodeCatalogItemResponse | undefined {
    return this._catalog().find(
      (item) => item.nodeType === nodeType && (item.nodeSubtype ?? null) === (nodeSubtype ?? null)
    );
  }
}
